import React from 'react';
import { Box, Typography } from '@mui/material';
import LanguageAvatar from 'components/LanguageAvatar';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

/**
 * ClassHeader renders a gradient banner for music category pages,
 * using the OwnerHeader gradient style and TitleHeader typography.
 */
const HeaderBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'lingo',
})<{
  lingo?: string;
}>(({ theme, lingo }) => ({
  background:
    lingo?.toLowerCase() === 'uk'
      ? 'linear-gradient(to right, #1e40af, #ffd700)'
      : 'linear-gradient(to right, #1e40af, #ef233c)',
  color: theme.palette.common.white,
  borderBottomLeftRadius: theme.shape.borderRadius * 2,
  borderBottomRightRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[2],
  // Fixed height; content vertically centered
  height: theme.spacing(17),
  display: 'flex',
  alignItems: 'center',
}));

interface ClassHeaderProps {
  /** Title text for the class header */
  title: React.ReactNode;
  /** Language code (lingo) to determine header style */
  lingo?: string;
}

export default function ClassHeader({ title, lingo }: ClassHeaderProps) {
  // Determine if language avatar should be shown
  const { i18n } = useTranslation();
  // Active user language code
  const currentLang = (i18n.language || 'en').split('-')[0];
  // Normalize class lingo code
  const langCode = (lingo || '').split('-')[0].toLowerCase();
  // Show avatar if either the user language or class language is not English
  const showLangAvatar =
    langCode !== '' && (currentLang !== 'en' || langCode !== 'en');
  return (
    <HeaderBox lingo={lingo}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: { xs: 1, sm: 2 } }}>
        {showLangAvatar && (
          <LanguageAvatar language={langCode} sx={{ width: 72, height: 72 }} />
        )}
        <Typography
          variant="h3"
          component="h2"
          sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}
        >
          {title}
        </Typography>
      </Box>
    </HeaderBox>
  );
}